import { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useGetFieldSiteRdPositionsQuery,
  useSetFieldElementPositionMutation,
} from '@shared/api';
import {
  elementPositionResultText,
  rdPositionChoiceLabel,
  rdPositionChoiceNotes,
  rdPositionChoices,
  type FieldElement,
  type RdPositionChoice,
} from '@entities/field';
import { Button } from '@shared/ui/Button';
import { Combobox, type ComboboxOption } from '@shared/ui/Combobox';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const Note = styled.div<{ $ok: boolean }>`
  font-size: 13px;
  color: ${({ theme, $ok }) => ($ok ? theme.colors.status.success : theme.colors.status.danger)};
`;

/** Привязка сохранена, но принятые факты остались на прежней позиции РД. */
const Warn = styled.div.attrs({ role: 'status' })`
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.status.warning};
`;

const Hint = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/**
 * Вариант позиции РД → строка выбора: номер строки ведомости объёмов ПТО
 * («СП01 №5»), наименование (марка, пометка сваи) с количеством и единицей — они
 * же в «чипе» выбранного (у рядовых и испытуемых свай одной марки наименования
 * совпадают), ниже — часть РД, где в РД, сколько дублей склеено, нет ли позиции в
 * последнем извлечении РД, нужна ли проверка и решение ПТО по позиции. Номера и
 * части нет (старый сервер) — без них.
 */
function positionOption(c: RdPositionChoice): ComboboxOption {
  const row = c.position.row_label?.trim();
  const label = rdPositionChoiceLabel(c);
  return {
    id: c.position.id,
    label: row ? `${row} · ${label}` : label,
    sublabel: [c.position.part_label?.trim(), ...rdPositionChoiceNotes(c)]
      .filter(Boolean)
      .join(' · '),
  };
}

/**
 * Перепривязка элемента конструктива к позиции объёма РД. Нужна, когда
 * «Извлечь объёмы»/переиндексация ревизии не нашла однозначного двойника
 * прежней позиции и привязку сняла: без позиции у фактов элемента нет сверки с
 * РД, а в АОСР — строк. Факты элемента без позиции (с их строками ОЖР) и факты
 * без решения ПТО с прежней позиции сервер переносит на выбранную — итог
 * «Перенесено фактов: N». Принятые факты на прежней позиции остаются там
 * (решения ПТО и строки ОЖР не переносятся) — об этом предупреждение
 * ([`elementPositionResultText`]). Показывается только проверяющему ПТО.
 * Варианты — как у справочника конструктива из РД ([`rdPositionChoices`]): с
 * количеством, без заголовков и отбракованных, дубли текст/таблица — одной строкой.
 * Позицию, которую модель при последнем «Извлечь объёмы» не назвала (оставлена
 * из-за привязок), выбор помечает — её сверяют с листом до привязки.
 */
export function ElementPositionPicker({
  siteId,
  element,
}: {
  siteId: string;
  element: FieldElement;
}) {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState('');
  const [note, setNote] = useState<{ ok: boolean; text: string; warning?: string | null } | null>(
    null,
  );
  const positionsQ = useGetFieldSiteRdPositionsQuery(siteId, { skip: !open });
  const [setPosition, { isLoading: saving }] = useSetFieldElementPositionMutation();

  const all = positionsQ.data?.positions;
  const choices = useMemo(() => rdPositionChoices(all ?? []), [all]);
  const options = useMemo(() => choices.map(positionOption), [choices]);
  const chosenOption = options.find((o) => o.id === chosen);
  const hidden = (all?.length ?? 0) - choices.reduce((n, c) => n + c.merged, 0);

  async function onSave() {
    if (!chosen) return;
    setNote(null);
    try {
      const res = await setPosition({ elementId: element.id, position_id: chosen }).unwrap();
      setNote({ ok: true, ...elementPositionResultText(res) });
      setOpen(false);
      setChosen('');
    } catch (e) {
      setNote({ ok: false, text: apiErrorText(e, 'Не удалось привязать элемент к позиции РД.') });
    }
  }

  const noteView = note && (
    <>
      <Note $ok={note.ok}>{note.text}</Note>
      {note.warning && <Warn>{note.warning}</Warn>}
    </>
  );

  if (!open) {
    return (
      <Wrap>
        <Row>
          <Button type="button" $variant="ghost" $size="sm" onClick={() => setOpen(true)}>
            {element.position_id ? 'Сменить позицию РД…' : 'Привязать к позиции РД…'}
          </Button>
        </Row>
        {noteView}
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Combobox
        id={`element-position-${element.id}`}
        value={chosen}
        selectedLabel={chosenOption?.label}
        options={options}
        onChange={(o) => setChosen(o?.id ?? '')}
        placeholder="Выберите позицию объёма РД…"
        emptyText={
          positionsQ.isError
            ? apiErrorText(positionsQ.error, 'Позиции РД не загрузились')
            : all && all.length === 0
              ? // Сервер сам отсекает заголовки таблиц, итоги и отбракованные позиции.
                'В действующих ревизиях РД проекта нет позиций объёма'
              : all && choices.length === 0
                ? 'В РД только заголовки таблиц или отбракованные позиции'
                : 'Позиция не найдена'
        }
        loading={positionsQ.isFetching}
      />
      {hidden > 0 && (
        <Hint>
          Не показаны заголовки таблиц, итоги и отбракованные инженером позиции: {hidden}.
        </Hint>
      )}
      <Row>
        <Button type="button" $size="sm" disabled={!chosen || saving} onClick={() => void onSave()}>
          {saving ? 'Привязываю…' : 'Привязать'}
        </Button>
        <Button type="button" $variant="ghost" $size="sm" onClick={() => setOpen(false)}>
          Отмена
        </Button>
      </Row>
      {noteView}
    </Wrap>
  );
}
